import * as XLSX from 'xlsx';

import type { AgreementImportSourceRow } from '@client/api';

/**
 * Column headers expected in the uploaded CSV/XLSX file, matched case-insensitively
 * and with whitespace/underscores/dashes stripped. Mirrors <c>AgreementImportSourceRow</c>
 * (server/Renturio.Application/Commands/Agreement/AgreementImport/ImportAgreementRows.cs).
 */
const COLUMN_ALIASES: Record<keyof AgreementImportSourceRow, string[]> = {
  documentId: ['documentid', 'document_id', 'document id', 'contractid', 'contract_id'],
  tenantName: ['tenantname', 'tenant_name', 'tenant name', 'leietaker'],
  managerName: ['managername', 'manager_name', 'manager name', 'forvalter'],
  durationStartDate: [
    'durationstartdate',
    'duration_start_date',
    'duration_start',
    'durationstart',
    'startdate',
    'start_date',
    'startdato',
  ],
  durationEndDate: [
    'durationenddate',
    'duration_end_date',
    'duration_end',
    'durationend',
    'enddate',
    'end_date',
    'sluttdato',
  ],
  indexAdjustment: [
    'indexadjustment',
    'index_adjustment',
    'index_adjustment_per_interval',
    'indexadjustmentperinterval',
    'indeksregulering',
  ],
  indexBaseDate: ['indexbasedate', 'index_base_date', 'indeksbasedato', 'indeksbase'],
  indexBaseValue: ['indexbasevalue', 'index_base_value', 'indeksbaseverdi'],
  indexInterval: ['indexinterval', 'index_interval', 'indeksintervall'],
  indexStartDate: ['indexstartdate', 'index_start_date', 'indeksstartdato'],
  indexType: ['indextype', 'index_type', 'indekstype'],
  invoicing: ['invoicing', 'fakturering'],
  optionLength: ['optionlength', 'option_length', 'opsjonslengde'],
  notificationDeadlineMonths: ['notificationdeadlinemonths', 'notification_deadline_months', 'varslingsfrist'],
  securityAmount: ['securityamount', 'security_amount', 'belop_sikkerhet', 'sikkerhetsbelop'],
  securityType: ['securitytype', 'security_type', 'sikkerhetstype'],
  spaceName: ['spacename', 'space_name', 'space name', 'areal', 'lokal'],
  area: ['area', 'areal_m2', 'areakvm'],
  amount: ['amount', 'belop', 'beløp'],
  unit: ['unit', 'enhet'],
  parkingRate: ['parkingrate', 'parking_rate', 'parkeringssats'],
};

function normalizeHeader(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

function buildHeaderMap(headers: string[]): Partial<Record<keyof AgreementImportSourceRow, string>> {
  const normalizedToOriginal = new Map<string, string>();
  for (const header of headers) {
    normalizedToOriginal.set(normalizeHeader(header), header);
  }

  const map: Partial<Record<keyof AgreementImportSourceRow, string>> = {};
  for (const field of Object.keys(COLUMN_ALIASES) as Array<keyof AgreementImportSourceRow>) {
    const aliases = COLUMN_ALIASES[field].map(normalizeHeader);
    const matchedOriginal = aliases.map((alias) => normalizedToOriginal.get(alias)).find((v) => v != null);
    if (matchedOriginal) map[field] = matchedOriginal;
  }
  return map;
}

function toStringOrNull(value: unknown): string | null {
  if (value == null) return null;
  const str = String(value).trim();
  return str.length > 0 ? str : null;
}

export interface ParseSourceFileResult {
  rows: AgreementImportSourceRow[];
  /** Rows skipped because they were missing the required <c>documentId</c>. */
  skippedRowCount: number;
}

/**
 * Parses an uploaded CSV/XLS/XLSX file into <c>AgreementImportSourceRow[]</c> client-side.
 * The backend's <c>POST /agreement-imports/{id}/rows</c> endpoint expects already-structured
 * rows (one row per space line, grouped later via <c>documentId</c>) rather than a raw file,
 * so parsing happens here rather than server-side.
 */
export async function parseSourceFile(file: File): Promise<ParseSourceFileResult> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[firstSheetName];

  const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: null, raw: false });
  if (rawRows.length === 0) return { rows: [], skippedRowCount: 0 };

  const headers = Object.keys(rawRows[0]);
  const headerMap = buildHeaderMap(headers);

  const rows: AgreementImportSourceRow[] = [];
  let skippedRowCount = 0;

  for (const rawRow of rawRows) {
    const documentIdHeader = headerMap.documentId;
    const documentId = documentIdHeader ? toStringOrNull(rawRow[documentIdHeader]) : null;
    if (!documentId) {
      skippedRowCount += 1;
      continue;
    }

    rows.push({
      documentId,
      tenantName: headerMap.tenantName ? toStringOrNull(rawRow[headerMap.tenantName]) : null,
      managerName: headerMap.managerName ? toStringOrNull(rawRow[headerMap.managerName]) : null,
      durationStartDate: headerMap.durationStartDate ? toStringOrNull(rawRow[headerMap.durationStartDate]) : null,
      durationEndDate: headerMap.durationEndDate ? toStringOrNull(rawRow[headerMap.durationEndDate]) : null,
      indexAdjustment: headerMap.indexAdjustment ? toStringOrNull(rawRow[headerMap.indexAdjustment]) : null,
      indexBaseDate: headerMap.indexBaseDate ? toStringOrNull(rawRow[headerMap.indexBaseDate]) : null,
      indexBaseValue: headerMap.indexBaseValue ? toStringOrNull(rawRow[headerMap.indexBaseValue]) : null,
      indexInterval: headerMap.indexInterval ? toStringOrNull(rawRow[headerMap.indexInterval]) : null,
      indexStartDate: headerMap.indexStartDate ? toStringOrNull(rawRow[headerMap.indexStartDate]) : null,
      indexType: headerMap.indexType ? toStringOrNull(rawRow[headerMap.indexType]) : null,
      invoicing: headerMap.invoicing ? toStringOrNull(rawRow[headerMap.invoicing]) : null,
      optionLength: headerMap.optionLength ? toStringOrNull(rawRow[headerMap.optionLength]) : null,
      notificationDeadlineMonths: headerMap.notificationDeadlineMonths
        ? toStringOrNull(rawRow[headerMap.notificationDeadlineMonths])
        : null,
      securityAmount: headerMap.securityAmount ? toStringOrNull(rawRow[headerMap.securityAmount]) : null,
      securityType: headerMap.securityType ? toStringOrNull(rawRow[headerMap.securityType]) : null,
      spaceName: headerMap.spaceName ? toStringOrNull(rawRow[headerMap.spaceName]) : null,
      area: headerMap.area ? toStringOrNull(rawRow[headerMap.area]) : null,
      amount: headerMap.amount ? toStringOrNull(rawRow[headerMap.amount]) : null,
      unit: headerMap.unit ? toStringOrNull(rawRow[headerMap.unit]) : null,
      parkingRate: headerMap.parkingRate ? toStringOrNull(rawRow[headerMap.parkingRate]) : null,
    });
  }

  return { rows, skippedRowCount };
}
